import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { Card, PageHeader, Badge } from '../components/Layout';

const Stat = ({ label, value, color = 'var(--accent)', icon }) => (
  <Card style={{ borderTop: `3px solid ${color}`, textAlign: 'center' }}>
    <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{icon} {label}</div>
    <div style={{ fontSize: 34, fontWeight: 800, color, fontFamily: "'Space Mono', monospace" }}>{value ?? '—'}</div>
  </Card>
);

const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function Dashboard() {
  const { company, mode } = useOutletContext();
  const [stats,      setStats]      = useState(null);
  const [activity,   setActivity]   = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [dateRange,  setDateRange]  = useState('7d');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sRes, aRes] = await Promise.all([
          api.get(`/analytics/summary?company=${encodeURIComponent(company)}&range=${dateRange}`),
          api.get(`/leads?company=${encodeURIComponent(company)}&limit=5`),
        ]);
        setStats(sRes.data);
        setActivity(aRes.data.leads || []);
      } catch { /* show empty state */ }

      // Load activity feed from calls
      try {
        const cRes = await api.get(`/calls?company=${encodeURIComponent(company)}`);
        setActivities(cRes.data || []);
      } catch { setActivities([]); }

      setLoading(false);
    };
    load();
  }, [company, dateRange]);

  const chartData = stats?.recentActivity?.map(r => ({ date: r._id.slice(5), actions: r.count })) || [];

  return (
    <div style={{ padding: 28 }}>
      <PageHeader
        title={`${company}`}
        sub={`${mode === 'bdm' ? '🚀 BDM / Software Projects' : '👔 US Staffing / C2C Recruiting'} — running 24/7`}
      />

      {/* Date range tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {[['7d', '7 Days'], ['30d', '30 Days'], ['90d', '90 Days']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setDateRange(val)}
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: dateRange === val ? 'var(--accent)' : 'var(--surface)',
              color: dateRange === val ? '#000' : 'var(--muted)',
              border: `1px solid ${dateRange === val ? 'var(--accent)' : 'var(--border)'}`,
            }}
          >{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'var(--muted)', padding: 40, textAlign: 'center' }}>Loading...</div>
      ) : (
        <>
          {/* Stats grid — 5 columns */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
            <Stat icon="👥" label="Total Leads"     value={stats?.totalLeads}      color="var(--accent)" />
            <Stat icon="✉️" label="Emails Sent"     value={stats?.emailsSent}      color="var(--amber)"  />
            <Stat icon="📞" label="Calls Made"      value={stats?.callsMade}       color="var(--green)"  />
            <Stat icon="🤝" label="Meetings Booked" value={stats?.meetingsBooked}  color="var(--purple)" />
            <Stat icon="💰" label="Pipeline Value"  value={stats?.pipelineValue ? `$${(stats.pipelineValue / 1000).toFixed(0)}k` : '$0'} color="var(--green)" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            {/* Chart */}
            <Card>
              <div style={{ fontWeight: 700, marginBottom: 16 }}>📈 Activity — Last {dateRange === '7d' ? '7' : dateRange === '30d' ? '30' : '90'} Days</div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="date" stroke="var(--muted)" tick={{ fontSize: 11 }} />
                    <YAxis stroke="var(--muted)" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8 }}
                      labelStyle={{ color: 'var(--text)' }}
                    />
                    <Bar dataKey="actions" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 40, fontSize: 13 }}>
                  No activity yet. Add leads and launch sequences to get started.
                </div>
              )}
            </Card>

            {/* Breakdown */}
            <Card>
              <div style={{ fontWeight: 700, marginBottom: 16 }}>🎯 Lead Breakdown</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { label: 'BDM Leads',        value: stats?.bdmLeads,    color: 'var(--accent)',  total: stats?.totalLeads || 1 },
                  { label: 'Recruiting Leads',  value: stats?.recLeads,    color: 'var(--purple)',  total: stats?.totalLeads || 1 },
                  { label: 'Replied',           value: stats?.emailsSent,  color: 'var(--amber)',   total: Math.max(stats?.totalLeads || 1, stats?.emailsSent || 1) },
                  { label: 'Deals Won',         value: stats?.dealsWon,    color: 'var(--green)',   total: Math.max((stats?.dealsWon || 0), 1) * 5 },
                ].map(item => (
                  <div key={item.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
                      <span style={{ color: 'var(--mid)' }}>{item.label}</span>
                      <span style={{ fontWeight: 700, color: item.color }}>{item.value ?? 0}</span>
                    </div>
                    <div style={{ height: 5, background: 'var(--border)', borderRadius: 3 }}>
                      <div style={{ height: '100%', width: `${item.total ? Math.min(100, ((item.value || 0) / item.total) * 100) : 0}%`, background: item.color, borderRadius: 3, transition: 'width 1s' }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Activity Feed */}
          <Card style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 700, marginBottom: 16 }}>⚡ Recent Activity</div>
            {activities.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                No activity yet. Start sending emails or making calls.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {activities.slice(0, 8).map((act, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)22' }}>
                    <span style={{ fontSize: 18 }}>
                      {act.type === 'email_sent' ? '✉️' : act.type === 'call_made' ? '📞' : act.type === 'reply_received' ? '💬' : '📌'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{act.leadName || 'Unknown lead'}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{act.subject || act.type?.replace(/_/g, ' ')}</div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{act.createdAt ? timeAgo(act.createdAt) : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent leads */}
          <Card>
            <div style={{ fontWeight: 700, marginBottom: 16 }}>🕒 Recent Leads Added</div>
            {activity.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: 30 }}>
                No leads yet. Go to Leads → Add Lead to get started.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Name', 'Title / Skill', 'Company', 'Type', 'Status'].map(h => (
                      <th key={h} style={{ color: 'var(--muted)', fontWeight: 600, padding: '6px 12px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: .5, borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activity.map((lead, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)22' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{lead.name}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--mid)' }}>{lead.title || lead.skill}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--muted)' }}>{lead.firmName}</td>
                      <td style={{ padding: '10px 12px' }}><Badge label={lead.type} color={lead.type === 'bdm' ? 'var(--accent)' : 'var(--purple)'} /></td>
                      <td style={{ padding: '10px 12px' }}><Badge label={lead.status} color="var(--green)" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
